// PenSettingsModal — `PenSettingsPanel` を載せる独立モーダル外枠
// (consult 2026-06-13 続報、modern-layout.md §1.1 表現パリティ)。
//
// 題は「描画の設定」(consult 2026-06-27、上部バー「描画」ボタンから開く)。手ぶれ補正 /
// AA に加え、旧「表示」モーダルから移したブラシカーソル / スポイトの取得元を束ねるため、
// 旧称「ペンの設定」より広い「描画」を冠する。「ショートカット」ボタン + `ShortcutSettingsModal`
// の前例を完全踏襲: ボタン配置 (表示 / ショートカットの並び、scope 'personal')・
// モーダル外枠 (backdrop / 見出し / ✕ / Esc)・state 管理 (penSettingsOpen) が同型。
//
// 役割分担:
//   - 本 component = モーダルの **外枠 chrome** だけ (UserPreferencesModal /
//     ShortcutSettingsModal の外枠と統一感を持たせる = 同じ class 構造を流用)。
//   - 中身 = self-contained な `PenSettingsPanel` (手ぶれ補正 + AA、値は共有 chrome の
//     controlled。将来は β の筆圧カーブ本格 UI 等もこのモーダルが受け皿)。
//
// 二段構え (ShortcutSettingsModal と同流儀): 将来「設定」タブ shell に畳むとき、本
// モーダル外枠を捨てて `PenSettingsPanel` をタブ body に **そのまま再マウント** する
// だけで済む (= パネルが外枠と疎結合なので作り直し不要)。

import { useEffect, useId } from 'react';
import { PenSettingsPanel, type PenSettingsPanelProps } from './PenSettingsPanel.js';

export interface PenSettingsModalProps extends PenSettingsPanelProps {
  /** Modal を閉じる。 */
  readonly onClose: () => void;
}

export const PenSettingsModal = ({
  onClose,
  ...panelProps
}: PenSettingsModalProps): React.ReactNode => {
  const titleId = useId();

  // Esc で閉じる (ShortcutSettingsModal / UserPreferencesModal と同パターン)。
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      {/* Backdrop (ShortcutSettingsModal と同流儀の a11y compliant button)。 */}
      <button
        type="button"
        aria-label="閉じる"
        tabIndex={-1}
        onClick={onClose}
        className="-z-10 absolute inset-0 cursor-default bg-black/60"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        // w-full max-w: max-w-full → max-w-[360px] に統一 (2026-07-03 一括点検、
        // 親 p-4 の余白内に収める。デスクトップは同幅)。
        className="max-h-[90vh] w-full max-w-[360px] overflow-y-auto rounded border border-white/10 bg-[var(--color-panel)] p-6 shadow-xl"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 id={titleId} className="font-bold text-[var(--color-accent)] text-base">
            描画の設定
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="閉じる"
            className="rounded px-2 py-1 text-[var(--color-text-muted)] text-sm hover:bg-white/10"
          >
            ✕
          </button>
        </div>
        <PenSettingsPanel {...panelProps} />
      </div>
    </div>
  );
};
